import { Children, Component, createRef } from "react";
import { Link } from "react-router-dom";

import { Page, PageInner, PageSection, TooltipWrapper } from "@app/shared";
import { QESButton, WizardNavigationBar, WizardSteps } from "@app/shared/qes-standards";
import { focusFirstInteractiveElement } from "@app/shared/utility";

interface AccessReportsWizardStepProps {
  children?: React.ReactNode;
  currentWizardStep: number;
  nextPath?: string;
  nextPathDisabled?: boolean;
  nextPathDisabledTooltipText?: string;
  onSubmitClick?: () => void;
  previousPath?: string;
  setWizardStep: (page: number) => void;
  wizardSteps: string[];
}

interface AccessReportsWizardStepState {
  submitting: boolean;
}

class AccessReportsWizardStep extends Component<AccessReportsWizardStepProps, AccessReportsWizardStepState> {
  private wizardBodyRef = createRef<HTMLDivElement>();

  constructor(props: AccessReportsWizardStepProps) {
    super(props);

    this.state = {
      submitting: false,
    };
  }

  public componentDidMount() {
    focusFirstInteractiveElement(this.wizardBodyRef);
  }

  public componentDidUpdate(prevProps: AccessReportsWizardStepProps) {
    if (prevProps.currentWizardStep !== this.props.currentWizardStep) {
      focusFirstInteractiveElement(this.wizardBodyRef);
    }
  }

  public render() {
    const hasChildren = Children.count(this.props.children) > 0;

    return (
      <Page className="bg-white" documentTitle="New Access Report">
        <PageInner>
          <PageSection>
            <div className="row">
              <div className="col-12">
                <h2 className="mt-3 mb-3 header-l font-weight-bold">
                  New Access Report
                </h2>
              </div>
            </div>

            <div className="row">
              <div className="col-11">
                <WizardSteps
                  currentStep={this.props.currentWizardStep}
                  setStep={this.props.setWizardStep}
                  steps={this.props.wizardSteps}
                />
              </div>

              <div className="col-12">
                <hr />
              </div>
            </div>

            <div ref={this.wizardBodyRef}>
              {hasChildren && this.props.children}
            </div>

            <div className="row">
              <div className="col-12">
                <hr />
              </div>
            </div>

          </PageSection>
        </PageInner>
        <WizardNavigationBar
          leftSide={
            <Link
              className="qes-button qes-button-secondary danger"
              id="access-reports-wizard__step--cancel"
              to={{
                pathname: `/access-reports`,
              }}
            >
              <b>Cancel</b>
            </Link>
          }
          rightSide={
            <div>
              {this.props.previousPath &&
                <Link
                  className="text-info"
                  id="access-reports-wizard__step--previous"
                  to={{
                    pathname: `${this.props.previousPath}`,
                  }}
                >
                  <b>Previous</b>
                </Link>
              }

              {this.props.nextPath &&
                (this.props.nextPathDisabled ?
                  <TooltipWrapper
                    containerClassName="ml-5"
                    text={this.props.nextPathDisabledTooltipText}
                    position="top"
                  >
                    <div
                      id="access-reports-wizard__step--disabled-next"
                      className="btn qes-button qes-button-primary disabled pr-5 pl-5"
                    >
                      Next
                    </div>
                  </TooltipWrapper> :
                  <Link
                    id="access-reports-wizard__step--next"
                    className="btn qes-button qes-button-primary ml-5 pr-5 pl-5"
                    onClick={(e) => e.currentTarget.blur()}
                    to={{
                      pathname: `${this.props.nextPath}`,
                    }}
                  >
                    Next
                  </Link>)
              }

              {this.props.onSubmitClick && (
                <QESButton
                  className="btn cursor-pointer ml-5 pr-5 pl-5"
                  disabled={this.state.submitting}
                  id="access-reports-wizard__step--submit"
                  onClick={this.onSubmitClick}
                  qesTestId="access_reports_wizard_step_submit"
                  qesType="primary"
                >
                  Submit
                </QESButton>
              )}
            </div>
          }
        />
      </Page>
    );
  }

  private onSubmitClick = (): void => {
    this.setState((prevState) => ({
      ...prevState,
      submitting: true,
    }), this.props.onSubmitClick);
  }
}

export { AccessReportsWizardStep };
